
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Store,
  Users,
  Heart,
  LoaderCircle,
  Eye,
  EyeOff,
} from "lucide-react";

import { auth, db } from "../firebase";

const inputClass =
  "mt-2 w-full rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm text-stone-800 outline-none transition placeholder:text-stone-400 focus:border-rose-800 focus:ring-2 focus:ring-rose-100";

const labelClass = "block text-sm font-medium text-stone-700";

const initialReseller = {
  name: "",
  mobile: "",
  email: "",
  password: "",
  storeName: "",
  storeSlug: "",
  tagline: "Handpicked sarees for every occasion, delivered to your door",
  referralCode: "",
};

const initialShopOwner = {
  name: "",
  shopName: "",
  mobile: "",
  email: "",
  password: "",
  sareeTypes: "",
};

function getFirebaseError(error) {
  const messages = {
    "auth/email-already-in-use": "This email is already registered. Please sign in.",
    "auth/invalid-email": "Please enter a valid email address.",
    "auth/weak-password": "Please use a stronger password with at least 8 characters.",
    "auth/network-request-failed": "Network error. Please check your internet connection.",
    "auth/too-many-requests": "Too many attempts. Please try again later.",
    "permission-denied": "Permission denied. Please check your Firebase security rules.",
    "unavailable": "The service is temporarily unavailable. Please try again.",
  };

  return messages[error?.code] ||
    error?.message ||
    "Something went wrong. Please try again.";
}

function normalizeSlug(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}


async function readFunctionJson(response, functionName) {
  const raw = await response.text();

  try {
    return JSON.parse(raw);
  } catch {
    const localHint =
      window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
        ? ' For local testing, run "npx netlify dev" from the frontend project root and open http://localhost:8888.'
        : " Check that the Netlify Function is deployed and the function directory is configured correctly.";
    throw new Error(
      `${functionName} returned a non-JSON response (HTTP ${response.status}).${localHint}`
    );
  }
}

const loadRazorpayCheckout = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve(window.Razorpay);
  const script = document.createElement("script");
  script.src = "https://checkout.razorpay.com/v1/checkout.js";
  script.onload = () => window.Razorpay ? resolve(window.Razorpay) : reject(new Error("Razorpay checkout did not load."));
  script.onerror = () => reject(new Error("Could not load Razorpay checkout."));
  document.body.appendChild(script);
});

export default function Signup() {


  const [role, setRole] = useState("");
  const [step, setStep] = useState(1);
  const [reseller, setReseller] = useState(initialReseller);
  const [shopOwner, setShopOwner] = useState(initialShopOwner);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showResellerPassword, setShowResellerPassword] = useState(false);

  const updateReseller = (event) => {
    const { name, value } = event.target;
    setError("");

    setReseller((current) => ({
      ...current,
      [name]: value,
      ...(name === "storeName" ? { storeSlug: normalizeSlug(value) } : {}),
      ...(name === "storeSlug" ? { storeSlug: normalizeSlug(value) } : {}),
    }));
  };

  const updateShopOwner = (event) => {
    setError("");
    setShopOwner((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  };

  const goToStep = (nextStep) => {
    setError("");
    setStep(nextStep);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleResellerDetails = (event) => {
    event.preventDefault();
    setError("");

    if (!/^[6-9]\d{9}$/.test(reseller.mobile.replace(/\D/g, ""))) {
      setError("Enter a valid 10-digit Indian mobile number.");
      return;
    }

    goToStep(2);
  };

  const handleStoreDetails = async (event) => {
    event.preventDefault();
    setError("");

    const slug = normalizeSlug(reseller.storeSlug);

    if (slug.length < 3) {
      setError("Your store link must have at least 3 characters.");
      return;
    }

    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      setError("Use only lowercase letters, numbers and single hyphens in your store link.");
      return;
    }

    setLoading(true);

    try {
      // Do not query the private users collection from signup: that query
      // requires list permission and was causing permission-denied errors.
      // Slug uniqueness should be enforced separately with a storeSlugs/{slug}
      // document/transaction when the matching Firestore rules are installed.
      setReseller((current) => ({ ...current, storeSlug: slug }));
      goToStep(3);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    let createdUser = null;
    try {
      const slug = normalizeSlug(reseller.storeSlug);
      const existingStore = await getDoc(doc(db, "storeSlugs", slug));
      if (existingStore.exists()) {
        setError("This store link is already taken. Please go back and choose another link.");
        setStep(2);
        return;
      }
      if (!process.env.REACT_APP_RAZORPAY_KEY_ID) throw new Error("Razorpay is not configured. Please contact support.");
      const orderResponse = await fetch("/.netlify/functions/create-order", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ purpose: "reseller_registration", email: reseller.email.trim(), name: reseller.name.trim(), phone: reseller.mobile.replace(/\D/g, "") })
      });
      const orderData = await readFunctionJson(orderResponse, "create-order");
      if (!orderResponse.ok) throw new Error(orderData.error || "Could not start subscription payment.");
      if (Number(orderData.amount) !== 49900) {
        throw new Error("Vasthra subscription server must return ₹499 (49,900 paise). No payment was opened.");
      }
      const RazorpayCtor = await loadRazorpayCheckout();
      await new Promise((resolve, reject) => {
        const checkout = new RazorpayCtor({
          key: process.env.REACT_APP_RAZORPAY_KEY_ID, amount: orderData.amount, currency: "INR",
          name: "Vasthra", description: "Vasthra monthly subscription — ₹499/month",
          order_id: orderData.razorpayOrderId,
          prefill: { name: reseller.name.trim(), email: reseller.email.trim(), contact: reseller.mobile.replace(/\D/g, "") },
          theme: { color: "#881337" },
          handler: async (payment) => {
            try {
              const credential = await createUserWithEmailAndPassword(auth, reseller.email.trim(), reseller.password);
              createdUser = credential.user;
              await updateProfile(createdUser, { displayName: reseller.name.trim() });
              const token = await createdUser.getIdToken();
              const verifyResponse = await fetch("/.netlify/functions/verify-payment", {
                method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  purpose: "reseller_registration", draftId: orderData.draftId,
                  razorpay_order_id: payment.razorpay_order_id,
                  razorpay_payment_id: payment.razorpay_payment_id,
                  razorpay_signature: payment.razorpay_signature,
                  reseller: {
                    name: reseller.name.trim(), email: reseller.email.trim().toLowerCase(),
                    mobile: reseller.mobile.replace(/\D/g, ""), storeName: reseller.storeName.trim(),
                    storeSlug: slug, tagline: reseller.tagline.trim(),
                    referralCode: reseller.referralCode.trim().toUpperCase()
                  }
                })
              });
              const verified = await readFunctionJson(verifyResponse, "verify-payment");
              if (!verifyResponse.ok) throw new Error(verified.error || "Payment verification failed.");
              setSubmitted(true);
              resolve();
            } catch (err) { reject(err); }
            finally { setLoading(false); }
          },
          modal: { ondismiss: () => { setLoading(false); reject(new Error("Payment was cancelled. You can try again when ready.")); } }
        });
        checkout.on("payment.failed", (failure) => { setLoading(false); reject(new Error(failure.error?.description || "Payment failed.")); });
        checkout.open();
      });
    } catch (err) {
      console.error("Reseller Razorpay signup:", err);
      setError(createdUser
        ? "Your login account was created, but payment verification/application setup needs support. Do not pay again. Contact support with your Razorpay payment ID."
        : (err.message || getFirebaseError(err)));
      setLoading(false);
    }
  };

  const handleShopOwnerSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    let createdUser = null;

    try {
      if (!/^[6-9]\d{9}$/.test(shopOwner.mobile.replace(/\D/g, ""))) {
        setError("Enter a valid 10-digit Indian mobile number.");
        return;
      }

      const credential = await createUserWithEmailAndPassword(
        auth,
        shopOwner.email.trim(),
        shopOwner.password
      );

      createdUser = credential.user;

      await updateProfile(createdUser, {
        displayName: shopOwner.name.trim(),
      });

      await setDoc(doc(db, "users", createdUser.uid), {
        uid: createdUser.uid,
        name: shopOwner.name.trim(),
        shopName: shopOwner.shopName.trim(),
        mobile: shopOwner.mobile.replace(/\D/g, ""),
        email: shopOwner.email.trim().toLowerCase(),
        role: "shop-owner",
        status: "pending",
        applicationStatus: "pending_review",
        sareeTypes: shopOwner.sareeTypes.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await setDoc(doc(db, "shopOwnerApplications", createdUser.uid), {
        uid: createdUser.uid,
        applicationStatus: "pending_review",
        submittedAt: serverTimestamp(),
      });

      setSubmitted(true);
    } catch (err) {
      console.error("Shop owner registration error:", err);

      if (createdUser) {
        setError(
          "Your login account was created, but your application could not be completed. Please contact support before registering again."
        );
      } else {
        setError(getFirebaseError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, label: "Your details" },
    { number: 2, label: "Your store" },
    { number: 3, label: "Payment" },
  ];

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#faf7f5] px-3 py-5 sm:px-6 sm:py-12">
      <div className="mx-auto max-w-3xl">
        {error && (
          <div
            role="alert"
            className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        <AnimatePresence mode="wait">
          {!role ? (
            <motion.div
              key="role-selection"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm sm:p-10"
            >
              <div className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-rose-900">
                  <Heart size={23} fill="currentColor" />
                </div>
                <h1 className="mt-4 font-serif text-3xl text-rose-950 sm:text-4xl">
                  Join Vasthra
                </h1>
                <p className="mt-3 text-sm leading-6 text-stone-600">
                  Choose how you would like to join our saree community.
                </p>
              </div>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => {
                    setError("");
                    setRole("reseller");
                  }}
                  className="group rounded-xl border border-stone-200 p-6 text-left transition hover:-translate-y-1 hover:border-rose-800 hover:shadow-lg"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-rose-50 text-rose-900">
                    <Users size={24} />
                  </div>
                  <h2 className="mt-5 font-serif text-xl text-rose-950">
                    Sign up as a Reseller
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-stone-600">
                    Start Your Saree Reselling Business at Just ₹499/Month! Create Your Store & Sell Easily.
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-rose-900">
                    Continue <ArrowRight size={16} />
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setError("");
                    setRole("shop-owner");
                  }}
                  className="group rounded-xl border border-stone-200 p-6 text-left transition hover:-translate-y-1 hover:border-rose-800 hover:shadow-lg"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-amber-800">
                    <Store size={24} />
                  </div>
                  <h2 className="mt-5 font-serif text-xl text-rose-950">
                    Apply as a Shop Owner
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-stone-600">
                    List your sarees on Vasthra, reach more customers, and grow your business.
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-rose-900">
                    Apply now <ArrowRight size={16} />
                  </span>
                </button>
              </div>

              <p className="mt-8 text-center text-sm text-stone-600">
                Already registered?{" "}
                <Link
                  to="/signin"
                  className="font-semibold text-rose-800 underline underline-offset-4"
                >
                  Sign in
                </Link>
              </p>
            </motion.div>
          ) : submitted ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              className="rounded-2xl border border-stone-200 bg-white p-8 text-center shadow-sm sm:p-12"
            >
              <CheckCircle2 size={54} className="mx-auto text-emerald-600" />
              <h1 className="mt-5 font-serif text-3xl text-rose-950">
                Application received
              </h1>
              <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-stone-600">
                Your Firebase account and application have been created.
                {role === "reseller"
                  ? " Your ₹499 subscription payment has been submitted for verification. Dashboard access remains pending until an authorized admin approves your application."
                  : " Your shop owner application is awaiting admin review. You cannot publish products until approved."}
              </p>
              <Link
                to="/signin"
                className="mt-7 inline-flex rounded-lg bg-rose-800 px-6 py-3 text-sm font-semibold text-white transition hover:bg-rose-950"
              >
                Go to Sign in
              </Link>
            </motion.div>
          ) : role === "reseller" ? (
            <motion.div
              key={`reseller-${step}`}
              initial={{ opacity: 0, x: 18 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -18 }}
              className="space-y-4"
            >
              <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
                <button
                  type="button"
                  onClick={() => {
                    setRole("");
                    setStep(1);
                    setError("");
                  }}
                  className="mb-4 inline-flex items-center gap-2 text-sm text-stone-600 hover:text-rose-900"
                >
                  <ArrowLeft size={16} /> Change account type
                </button>

                <h1 className="text-center font-serif text-3xl text-rose-950">
                  Start your saree business
                </h1>

                <div className="mt-7 grid grid-cols-3 gap-3">
                  {steps.map((item) => (
                    <div key={item.number} className="text-center">
                      <div
                        className={`mx-auto flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold ${
                          step === item.number
                            ? "bg-amber-300 text-rose-950 ring-4 ring-amber-100"
                            : step > item.number
                              ? "bg-emerald-600 text-white"
                              : "bg-stone-200 text-stone-500"
                        }`}
                      >
                        {step > item.number ? (
                          <CheckCircle2 size={18} />
                        ) : (
                          item.number
                        )}
                      </div>
                      <p className="mt-2 text-xs text-stone-600">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {step === 1 && (
                <form
                  onSubmit={handleResellerDetails}
                  className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8"
                >
                  <h2 className="font-serif text-xl text-rose-950">
                    Step 1 · Your details
                  </h2>

                  <div className="mt-5 grid gap-5 sm:grid-cols-2">
                    <label className={labelClass}>
                      Full name *
                      <input
                        className={inputClass}
                        name="name"
                        value={reseller.name}
                        onChange={updateReseller}
                        autoComplete="name"
                        placeholder="e.g. Kavya Sri"
                        required
                      />
                    </label>

                    <label className={labelClass}>
                      Mobile / WhatsApp number *
                      <input
                        className={inputClass}
                        name="mobile"
                        value={reseller.mobile}
                        onChange={updateReseller}
                        type="tel"
                        autoComplete="tel"
                        placeholder="9876543210"
                        required
                      />
                    </label>

                    <label className={labelClass}>
                      Email *
                      <input
                        className={inputClass}
                        name="email"
                        value={reseller.email}
                        onChange={updateReseller}
                        type="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        required
                      />
                    </label>

                    <label className={labelClass}>
                      Create password *
                      <div className="relative mt-2">
                        <input
                          className={`${inputClass} mt-0 pr-12`}
                          name="password"
                          value={reseller.password}
                          onChange={updateReseller}
                          type={showResellerPassword ? "text" : "password"}
                          autoComplete="new-password"
                          minLength={8}
                          required
                          placeholder="At least 8 characters"
                        />
                        <button
                          type="button"
                          onClick={() => setShowResellerPassword((visible) => !visible)}
                          className="absolute inset-y-0 right-0 inline-flex items-center justify-center px-3 text-stone-500 hover:text-rose-900"
                          aria-label={showResellerPassword ? "Hide password" : "Show password"}
                          aria-pressed={showResellerPassword}
                        >
                          {showResellerPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      <span className="mt-2 block text-xs text-stone-500">
                        At least 8 characters.
                      </span>
                    </label>
                  </div>

                  <div className="mt-7 flex justify-end">
                    <button
                      type="submit"
                      className="inline-flex items-center gap-2 rounded-lg bg-rose-800 px-6 py-3 text-sm font-semibold text-white hover:bg-rose-950"
                    >
                      Continue <ArrowRight size={16} />
                    </button>
                  </div>
                </form>
              )}

              {step === 2 && (
                <form
                  onSubmit={handleStoreDetails}
                  className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8"
                >
                  <h2 className="font-serif text-xl text-rose-950">
                    Step 2 · Your store
                  </h2>

                  <div className="mt-5 space-y-5">
                    <label className={labelClass}>
                      Shop name *
                      <input
                        className={inputClass}
                        name="storeName"
                        value={reseller.storeName}
                        onChange={updateReseller}
                        placeholder="e.g. Sai Silk Sarees"
                        required
                      />
                    </label>

                    <label className={labelClass}>
                      Your store link *
                      <div className="mt-2 flex overflow-hidden rounded-lg border border-stone-200 focus-within:border-rose-800">
                        <span className="flex items-center border-r border-stone-200 bg-stone-50 px-3 text-sm text-stone-500">
                          /s/
                        </span>
                        <input
                          className="min-w-0 flex-1 px-3 py-3 text-sm outline-none"
                          name="storeSlug"
                          value={reseller.storeSlug}
                          onChange={updateReseller}
                          placeholder="automatically-generated-store-link"
                          pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                          minLength={3}
                          maxLength={40}
                          readOnly
                          required
                        />
                      </div>
                      <span className="mt-2 block text-xs text-stone-500">
                        This link is generated automatically from your shop name. If it is already taken, change your shop name slightly.
                      </span>
                    </label>

                    <label className={labelClass}>
                      Tagline
                      <textarea
                        className={inputClass}
                        name="tagline"
                        value={reseller.tagline}
                        onChange={updateReseller}
                        rows={2}
                      />
                    </label>

                    <label className={labelClass}>
                      Referral code (optional)
                      <input
                        className={inputClass}
                        name="referralCode"
                        value={reseller.referralCode}
                        onChange={updateReseller}
                        placeholder="e.g. LAKSHMI1001"
                      />
                    </label>
                  </div>

                  <div className="mt-6 rounded-xl border border-amber-400 bg-[#fffdf8] p-4">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="font-semibold text-stone-800">
                        Monthly subscription
                      </h3>
                      <strong className="text-rose-900">₹499/month</strong>
                    </div>
                    <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-stone-600">
                      <li>Your own store link to share with customers</li>
                      <li>Access to participating shop owners' sarees</li>
                      <li>Wallet and withdrawal features subject to platform terms</li>
                      <li>Monthly subscription: ₹499</li>
                    </ul>
                  </div>

                  <div className="mt-7 flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => goToStep(1)}
                      className="rounded-lg border border-stone-200 px-5 py-3 text-sm font-semibold text-stone-700 hover:bg-stone-50"
                    >
                      <ArrowLeft className="mr-1 inline" size={15} />
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center gap-2 rounded-lg bg-amber-300 px-5 py-3 text-sm font-bold text-rose-950 hover:bg-amber-400 disabled:opacity-60"
                    >
                      {loading ? <LoaderCircle className="animate-spin" size={16} /> : null}
                      Continue to payment <ArrowRight size={16} />
                    </button>
                  </div>
                </form>
              )}

              {step === 3 && (
                <form
                  onSubmit={handlePaymentSubmit}
                  className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8"
                >
                  <h2 className="font-serif text-2xl text-rose-950">
                    Step 3 · Payment
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-stone-600">
                    Pay the ₹499 monthly subscription through Razorpay. Your application will be sent for admin review after payment verification. Subscription access lasts one month from activation.
                  </p>

                  <div className="mt-6 rounded-xl bg-rose-950 p-5 text-white">
                    <p className="text-sm text-amber-200">Monthly subscription</p>
                    <p className="mt-1 font-serif text-4xl">₹499</p><p className="mt-1 text-sm text-rose-100">Billed monthly</p>
                  </div>

                  <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <p className="font-semibold text-rose-950">Secure Razorpay checkout</p>
                    <p className="mt-1 text-sm text-stone-600">Click the payment button below to open Razorpay checkout. Do not pay by QR or upload a payment screenshot.</p>
                  </div>

                  <div className="mt-7 flex items-center justify-between gap-3">
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => goToStep(2)}
                      className="rounded-lg border border-stone-200 px-5 py-3 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-60"
                    >
                      <ArrowLeft className="mr-1 inline" size={15} />
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center gap-2 rounded-lg bg-rose-800 px-5 py-3 text-sm font-semibold text-white hover:bg-rose-950 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {loading && <LoaderCircle size={16} className="animate-spin" />}
                      {loading ? "Opening secure checkout..." : "Pay ₹499 with Razorpay"}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          ) : (
            <motion.form
              key="shop-owner"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handleShopOwnerSubmit}
              className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm sm:p-10"
            >
              <button
                type="button"
                onClick={() => {
                  setRole("");
                  setError("");
                }}
                className="mb-5 inline-flex items-center gap-2 text-sm text-stone-600 hover:text-rose-900"
              >
                <ArrowLeft size={16} /> Change account type
              </button>

              <h1 className="font-serif text-3xl text-rose-950">
                Apply as a shop owner
              </h1>
              <p className="mt-3 text-sm leading-6 text-stone-600">
                List sarees with wholesale prices and receive orders from
                resellers. Applications are reviewed before a shop goes live.
              </p>

              <div className="mt-7 grid gap-5 sm:grid-cols-2">
                <label className={labelClass}>
                  Your name *
                  <input
                    className={inputClass}
                    name="name"
                    value={shopOwner.name}
                    onChange={updateShopOwner}
                    autoComplete="name"
                    required
                  />
                </label>

                <label className={labelClass}>
                  Shop / brand name *
                  <input
                    className={inputClass}
                    name="shopName"
                    value={shopOwner.shopName}
                    onChange={updateShopOwner}
                    required
                  />
                </label>

                <label className={labelClass}>
                  Mobile number *
                  <input
                    className={inputClass}
                    name="mobile"
                    value={shopOwner.mobile}
                    onChange={updateShopOwner}
                    type="tel"
                    autoComplete="tel"
                    placeholder="9876543210"
                    required
                  />
                </label>

                <label className={labelClass}>
                  Email *
                  <input
                    className={inputClass}
                    name="email"
                    value={shopOwner.email}
                    onChange={updateShopOwner}
                    type="email"
                    autoComplete="email"
                    required
                  />
                </label>

                <label className={labelClass}>
                  Create password (8+ characters) *
                  <input
                    className={inputClass}
                    name="password"
                    value={shopOwner.password}
                    onChange={updateShopOwner}
                    type="password"
                    minLength={8}
                    autoComplete="new-password"
                    required
                  />
                </label>

                <label className={labelClass}>
                  Types of sarees you sell
                  <input
                    className={inputClass}
                    name="sareeTypes"
                    value={shopOwner.sareeTypes}
                    onChange={updateShopOwner}
                    placeholder="Kanchipuram, cotton, Kalamkari"
                  />
                </label>
              </div>

              <p className="mt-5 text-xs leading-5 text-stone-500">
                Your application remains pending until an authorized admin
                reviews it. Vasthra charges a 5% commission on every sale. Never enter payment or bank passwords here.
              </p>

              <button
                type="submit"
                disabled={loading}
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-rose-800 px-7 py-3.5 text-sm font-bold text-white hover:bg-rose-950 disabled:opacity-60"
              >
                {loading && <LoaderCircle size={16} className="animate-spin" />}
                {loading ? "Submitting..." : "Send application"}
                {!loading && <ArrowRight size={16} />}
              </button>

              <p className="mt-7 text-center text-sm text-stone-600">
                Already registered?{" "}
                <Link
                  to="/signin"
                  className="font-semibold text-rose-800 underline underline-offset-4"
                >
                  Sign in
                </Link>
              </p>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}
